"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wrDescriptionJunkReasons = wrDescriptionJunkReasons;
exports.wrPlainText = wrPlainText;
exports.wrWordCount = wrWordCount;
exports.wrIsTitleCaps = wrIsTitleCaps;
exports.wrNormTitle = wrNormTitle;
exports.wrDuplicateTitles = wrDuplicateTitles;
exports.wrScanIssues = wrScanIssues;
exports.wrIssueScore = wrIssueScore;
exports.wrLiveChecks = wrLiveChecks;
/**
 * Web Readiness checks — ONE implementation shared by tools-service (scan,
 * live checks, submit gate) and the platform-app product editor (the checklist
 * re-evaluates the UNSAVED form as you type). Pure, no I/O.
 *
 * tools-service/src/lib/logic.ts keeps an inline copy as its cold-start
 * fallback; logic.parity.test.ts there asserts both give identical results.
 */
const tools_schema_1 = require("./tools.schema");
// ── Description rules ─────────────────────────────────────────────────────────
/** Tags the product editor (Quill 2 via react-quill-new) legitimately emits. */
const WR_QUILL_TAGS = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a',
    'h1', 'h2', 'h3', 'span', 'blockquote', 'pre', 'code', 'sub', 'sup',
]);
/** Reasons a description is junk (empty array = clean). */
function wrDescriptionJunkReasons(html) {
    if (!html)
        return [];
    const out = [];
    if (/&lt;\s*\/?\s*[a-z]/i.test(html))
        out.push('escaped markup');
    if (/<font\b/i.test(html))
        out.push('<font> tags');
    if (/<span\b[^>]*\bstyle\s*=/i.test(html))
        out.push('styled spans');
    if (/<div\b[^>]*class\s*=\s*["']?[^"'>]*Mso/i.test(html))
        out.push('Word (Mso) markup');
    if (/<table\b/i.test(html))
        out.push('tables');
    if (/<[a-z][^>]*\sstyle\s*=/i.test(html))
        out.push('inline styles');
    if (/(&nbsp;\s*){2,}/i.test(html))
        out.push('&nbsp; runs');
    if (/(<br\s*\/?>\s*){3,}/i.test(html))
        out.push('3+ line breaks');
    if (/\\[nr]/.test(html))
        out.push('literal \\n');
    const foreign = new Set();
    for (const m of html.matchAll(/<\/?\s*([a-z][a-z0-9]*)\b/gi)) {
        const tag = m[1].toLowerCase();
        if (!WR_QUILL_TAGS.has(tag))
            foreign.add(tag);
    }
    if (foreign.size)
        out.push(`non-editor tags: ${[...foreign].slice(0, 5).join(', ')}`);
    return [...new Set(out)];
}
function wrPlainText(html) {
    if (!html)
        return '';
    return html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|li|h[1-6]|div)>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim();
}
function wrWordCount(html) {
    const t = wrPlainText(html);
    return t ? t.split(' ').filter((w) => /[a-z0-9]/i.test(w)).length : 0;
}
/**
 * ALL CAPS title: ≥ 6 letters and > 70% of them uppercase. Short all-caps
 * tokens (≤ 4 letters, e.g. RAW, USB, XL) are ignored when the rest of the
 * title has lowercase letters — "RAW Classic Cones" is fine.
 */
function wrIsTitleCaps(name) {
    if (!name)
        return false;
    const tokens = name.split(/\s+/).filter(Boolean);
    const hasLower = /[a-z]/.test(name);
    const counted = hasLower
        ? tokens.filter((t) => { const l = t.replace(/[^A-Za-z]/g, ''); return !(l.length <= 4 && l === l.toUpperCase()); })
        : tokens;
    const letters = counted.join('').replace(/[^A-Za-z]/g, '');
    if (letters.length < 6)
        return false;
    const upper = letters.replace(/[^A-Z]/g, '').length;
    return upper / letters.length > 0.7;
}
function wrNormTitle(name) {
    return (name || '').toLowerCase().replace(/\s+/g, ' ').trim();
}
/** Titles used by 2+ products in the catalog (normalised). */
function wrDuplicateTitles(products) {
    const counts = new Map();
    for (const p of products) {
        const n = wrNormTitle(p.name);
        if (n)
            counts.set(n, (counts.get(n) || 0) + 1);
    }
    return new Set([...counts].filter(([, c]) => c > 1).map(([n]) => n));
}
// ── Issue scanner + score ─────────────────────────────────────────────────────
function wrScanIssues(p, dupTitles = new Set()) {
    const issues = [];
    // Per-variant photos only matter when the product really has variants
    // (a single default variant is covered by the main photo).
    if (p.variants.length >= 2) {
        const missing = p.variants.filter((v) => v.images.length === 0);
        if (missing.length) {
            issues.push({
                code: 'variant_missing_image',
                detail: `${missing.length} of ${p.variants.length} variants`,
                variantIds: missing.map((v) => v.variantId),
            });
        }
    }
    if (p.images.length === 0)
        issues.push({ code: 'no_main_image' });
    const junk = wrDescriptionJunkReasons(p.description);
    if (junk.length)
        issues.push({ code: 'description_junk', detail: junk.join(', ') });
    const words = wrWordCount(p.description);
    if (words < 40)
        issues.push({ code: 'description_short', detail: `${words} words` });
    if (wrIsTitleCaps(p.name))
        issues.push({ code: 'title_caps' });
    if (!p.categoryId)
        issues.push({ code: 'no_category' });
    if (!p.brandId)
        issues.push({ code: 'no_brand' });
    if (dupTitles.has(wrNormTitle(p.name)))
        issues.push({ code: 'duplicate_title' });
    const priced = (p.price ?? 0) > 0 || p.variants.some((v) => (v.price ?? 0) > 0);
    if (!priced)
        issues.push({ code: 'no_price' });
    return issues;
}
function wrIssueScore(issues) {
    return issues.reduce((s, i) => s + (tools_schema_1.WR_ISSUES[i.code]?.weight ?? 0), 0);
}
/** Every issue code as a pass/fail row (the ProductForm checklist). */
function wrLiveChecks(p, dupTitles = new Set()) {
    const issues = wrScanIssues(p, dupTitles);
    const byCode = new Map(issues.map((i) => [i.code, i]));
    return Object.keys(tools_schema_1.WR_ISSUES).map((code) => {
        const hit = byCode.get(code);
        return { code, label: tools_schema_1.WR_ISSUES[code].label, blocking: tools_schema_1.WR_ISSUES[code].blocking, passing: !hit, ...(hit?.detail ? { detail: hit.detail } : {}) };
    });
}
//# sourceMappingURL=webReadinessChecks.js.map